import { Autocomplete, Popper, TextField, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { makeStyles } from 'react-scale/components/design-system';
import { defaultApiFetchErrorHandler } from 'react-scale/lib/api-fetch';
import { INamedObjectTypes } from '@scale/shared/types/lookup';

interface ISelectOption {
  name?: string;
  id: string;
}
interface IFullSelectOption extends ISelectOption {
  disabled?: boolean;
}

export type SelectOption = ISelectOption | IFullSelectOption;

export function SingleAutocomplete<Item extends SelectOption>(props: {
  selectedOption: Item | undefined;
  searchFunction?: (searchValue: string) => Promise<Item[]> | Item[];
  disabledOptionTooltip?: string;
  onChange: (option: Item | undefined) => void;
  placeholder?: string;
  objectType?: INamedObjectTypes;
  disabled?: boolean;
}) {
  const { selectedOption, searchFunction, onChange, placeholder, disabledOptionTooltip, disabled } =
    props;

  const classes = styles();

  const [searchValue, setSearchValue] = useState<string>(selectedOption ? selectedOption.id : '');
  const [loading, setLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<Item[]>(selectedOption ? [selectedOption] : []);
  const [curTimeout, setCurTimeout] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (curTimeout) {
      clearTimeout(curTimeout);
    }
    if (searchValue) {
      setLoading(true);
    }
    setOptions([]);

    const newTimeout = setTimeout(async () => {
      try {
        if (
          searchFunction &&
          searchValue &&
          (!selectedOption || searchValue !== selectedOption.id)
        ) {
          const fetchedOptions = await searchFunction(searchValue);
          setOptions([...fetchedOptions]);
        }
        setLoading(false);
      } catch (error) {
        defaultApiFetchErrorHandler(error, { handleHttp: true });
        setOptions(selectedOption ? [selectedOption] : []);
      }
      return [];
    }, 500);
    setCurTimeout(newTimeout);
  }, [searchValue]);

  return (
    <Autocomplete
      options={options}
      loading={loading}
      value={selectedOption ?? null}
      disabled={disabled}
      isOptionEqualToValue={(option, value) => value && option.id === value.id}
      getOptionDisabled={option => option && 'disabled' in option && !!option.disabled}
      renderOption={(props, option) => (
        <Tooltip
          title={
            'disabled' in option && option.disabled && disabledOptionTooltip
              ? disabledOptionTooltip
              : ''
          }
          placement="right"
          key={option.id}
        >
          <div>
            <li {...props}>{option.name}</li>
          </div>
        </Tooltip>
      )}
      filterOptions={x => x}
      PopperComponent={props => {
        return (
          <Popper
            {...props}
            style={{ width: 'auto' }}
            placement="bottom-start"
            nonce={undefined}
            onResize={undefined}
            onResizeCapture={undefined}
          />
        );
      }}
      onInputChange={(event, newInputValue) => setSearchValue(newInputValue)}
      onChange={(event, newItem) => onChange((newItem as Item | null) ?? undefined)}
      className="scaleui border rounded-lg border-neutral-300"
      classes={{
        inputRoot: classes.selectInputRoot,
        input: classes.selectInput,
        tag: classes.selectChip,
      }}
      getOptionLabel={(option: Item) => option.name ?? option.id}
      inputValue={searchValue ?? ''}
      renderInput={params => (
        <TextField
          autoFocus
          {...params}
          InputLabelProps={{
            ...params.InputLabelProps,
            shrink: false,
          }}
          sx={{
            '& .MuiInputBase-input': {
              width: `${Math.max(
                searchValue.length,
                `Search ${placeholder ?? ''}`.length,
              )}ch !important`,
            },
          }}
          placeholder={`Search ${placeholder ?? ''}`}
        />
      )}
    />
  );
}

const styles = makeStyles({
  selectInput: {
    background: 'white !important',
    borderRadius: '4px',
    padding: '0 8px !important',
    height: '30px !important',
    minWidth: '100px !important',
  },
  selectInputRoot: {
    paddingTop: '4px !important',
    paddingBottom: '4px !important',
    paddingLeft: '4px !important',
    background: '#E6E6E6',
    flexDirection: 'row',
    border: 'none',
    gap: '4px',
    borderRadius: '8px !important',
    display: 'flex !important',
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      borderRadius: '8px',
    },
    '& .MuiInputBase-input': {
      border: 'none',
      borderRadius: '8px',
    },
    '& .MuiIconButton-root svg': {
      fill: 'grey !important',
    },
  },
  selectChip: {
    maxWidth: '120px !important',
    margin: '0 !important',
    height: '30px !important',
    background: 'white !important',
    '& > span': {
      padding: '0 4px 0 8px',
    },
    '& > svg': {
      margin: '1px !important',
    },
  },
});
